const CONSENT_STORAGE_KEY = "sp_cookie_consent_v2";

export const FUNCTIONAL_STORAGE_KEYS = [
  "pp_recently_viewed",
  "pp_seller_slug_last_used",
  "pp_buyer_email",
  "pp_buyer_name",
  "pp_buyer_address",
  "pp_buyer_postal_code",
  "pp_buyer_city",
  "pp_buyer_phone",
];

const DEFAULT_CONSENT = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

const functionalSessionStore = new Map();

function canUseLocalStorage() {
  try { return typeof window !== "undefined" && typeof window.localStorage !== "undefined"; } catch { return false; }
}

export function getConsentStorageKey() {
  return CONSENT_STORAGE_KEY;
}

export function normalizeConsent(input) {
  return {
    ...DEFAULT_CONSENT,
    ...(input || {}),
    necessary: true,
  };
}

export function readStoredConsent() {
  if (!canUseLocalStorage()) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    return normalizeConsent(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function storeConsent(consent) {
  if (!canUseLocalStorage()) return;
  const normalized = normalizeConsent({
    ...consent,
    updatedAt: new Date().toISOString(),
  });
  try { window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(normalized)); window.localStorage.removeItem("sp_cookie_consent_v1"); } catch { /* keep the explicit choice in context for this visit */ }
}

export function hasConsent(category) {
  const consent = readStoredConsent();
  const normalized = normalizeConsent(consent);
  return normalized[category] === true;
}

export function hasFunctionalConsent() {
  return hasConsent("functional");
}

export function clearPersistentFunctionalStorage() {
  if (!canUseLocalStorage()) return;
  try {
    for (const key of FUNCTIONAL_STORAGE_KEYS) window.localStorage.removeItem(key);
  } catch { /* blocked browser storage */ }
  // Also clear the in-memory session store so data is not retained
  // and re-read from memory when functional consent is false.
  functionalSessionStore.clear();
}

export function getFunctionalStorageItem(key) {
  if (!FUNCTIONAL_STORAGE_KEYS.includes(key)) return null;

  if (hasFunctionalConsent()) {
    if (!canUseLocalStorage()) return functionalSessionStore.get(key) ?? null;
    return window.localStorage.getItem(key);
  }

  return functionalSessionStore.get(key) ?? null;
}

export function setFunctionalStorageItem(key, value) {
  if (!FUNCTIONAL_STORAGE_KEYS.includes(key)) return;

  if (hasFunctionalConsent()) {
    if (canUseLocalStorage()) {
      window.localStorage.setItem(key, value);
    }
    functionalSessionStore.delete(key);
    return;
  }

  functionalSessionStore.set(key, value);
}

export function removeFunctionalStorageItem(key) {
  if (!FUNCTIONAL_STORAGE_KEYS.includes(key)) return;

  if (canUseLocalStorage()) {
    window.localStorage.removeItem(key);
  }
  functionalSessionStore.delete(key);
}

export function getFunctionalStorageJSON(key, fallback = null) {
  const raw = getFunctionalStorageItem(key);
  if (!raw) return fallback;

  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

export function setFunctionalStorageJSON(key, value) {
  setFunctionalStorageItem(key, JSON.stringify(value));
}

export const COOKIE_POLICY_ITEMS = [
  {
    key: CONSENT_STORAGE_KEY,
    category: "Nödvändig",
    purpose: "Sparar ditt val av cookie- och lagringsinställningar så att bannern inte behöver visas på nytt varje gång.",
    duration: "12 månader eller tills användaren ändrar sitt val",
    storage: "localStorage",
    consentRequired: false,
  },
  {
    key: "sb-<project-ref>-auth-token",
    category: "Nödvändig",
    purpose: "Håller användaren inloggad via Supabase auth-session.",
    duration: "Tills sessionen löper ut eller användaren loggar ut",
    storage: "localStorage",
    consentRequired: false,
  },
  {
    key: "pp_cart",
    category: "Nödvändig",
    purpose: "Sparar varukorgen så att köpflödet fungerar mellan sidvisningar.",
    duration: "Tills användaren rensar varukorgen eller lagringen tas bort",
    storage: "localStorage",
    consentRequired: false,
  },
  {
    key: "pp_seller_slug",
    category: "Nödvändig",
    purpose: "Behåller kopplingen till vald säljare/grupp så att rätt försäljning stöds i checkout.",
    duration: "Tills användaren byter eller rensar vald säljarkoppling",
    storage: "localStorage",
    consentRequired: false,
  },
  {
    key: "pp_recently_viewed",
    category: "Funktionell",
    purpose: "Visar nyligen tittade produkter för att göra det enklare att hitta tillbaka.",
    duration: "Tills användaren rensar listan eller återkallar samtycke",
    storage: "localStorage vid samtycke, annars endast minne under aktiv session",
    consentRequired: true,
  },
  {
    key: "pp_seller_slug_last_used",
    category: "Funktionell",
    purpose: "Minns senast använda säljarkoppling för snabbare återgång till checkout.",
    duration: "Tills värdet skrivs över eller samtycke återkallas",
    storage: "localStorage vid samtycke, annars endast minne under aktiv session",
    consentRequired: true,
  },
  {
    key: "pp_buyer_email / pp_buyer_name / pp_buyer_address / pp_buyer_postal_code / pp_buyer_city / pp_buyer_phone",
    category: "Funktionell",
    purpose: "Förifyller checkoutfält med tidigare angivna köpuppgifter för att förenkla nästa köp.",
    duration: "Tills värdena skrivs över eller samtycke återkallas",
    storage: "localStorage vid samtycke, annars endast minne under aktiv session",
    consentRequired: true,
  },
  {
    key: "Stripe.js (https://js.stripe.com/v3)",
    category: "Nödvändig tredjepart",
    purpose: "Laddas när checkout behöver betalgränssnitt för kort/Klarna via Stripe.",
    duration: "Laddas vid checkoutflöde",
    storage: "Tredjepartsscript",
    consentRequired: false,
  },
];
